import { useState, useEffect } from "react"
import { Users, Truck, ShoppingCart, TrendingUp, AlertCircle, MapPinned, Activity, Database, Wifi, CheckCircle2 } from "lucide-react"
import { API_ENDPOINTS } from "../config/api"
import { fetchWithAuth } from "../utils/api"
import { useLanguage } from "../i18n/LanguageContext"
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts"

export default function Dashboard() {
  const { language, t, translateApiMessage, translateStatus, translateGovernorate, formatNumber, formatCurrency, formatDateTime } = useLanguage()
  const [overview, setOverview] = useState(null)
  const [activities, setActivities] = useState([])
  const [bookingsTrend, setBookingsTrend] = useState([])
  const [revenueTrend, setRevenueTrend] = useState([])
  const [captains, setCaptains] = useState([])
  const [dailyReport, setDailyReport] = useState({ counters: {}, alerts: [], generated_at: null })
  const [health, setHealth] = useState({ api: "checking", operations: "checking", liveMap: "checking" })
  const [healthUpdatedAt, setHealthUpdatedAt] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    fetchDashboardData()
    // This dashboard intentionally loads once on mount; filters are handled by its endpoints.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    let mounted = true
    const checkHealth = async () => {
      const checks = await Promise.allSettled([
        fetchWithAuth(API_ENDPOINTS.DASHBOARD_OVERVIEW),
        fetchWithAuth(API_ENDPOINTS.DASHBOARD_OPERATIONS_SUMMARY),
        fetchWithAuth("https://toutsroutes.com/api/live-map/online-captains"),
      ])
      if (!mounted) return
      setHealth({
        api: checks[0].status === "fulfilled" && checks[0].value.ok ? "healthy" : "down",
        operations: checks[1].status === "fulfilled" && checks[1].value.ok ? "healthy" : "down",
        liveMap: checks[2].status === "fulfilled" && checks[2].value.ok ? "healthy" : "down",
      })
      setHealthUpdatedAt(new Date())
    }
    checkHealth()
    const timer = window.setInterval(checkHealth, 30000)
    return () => { mounted = false; window.clearInterval(timer) }
  }, [])

  const fetchDashboardData = async () => {
    try {
      // Fetch overview
      const overviewRes = await fetchWithAuth(API_ENDPOINTS.DASHBOARD_OVERVIEW)
      if (overviewRes.ok) {
        setOverview(await overviewRes.json())
      }

      const captainsRes = await fetchWithAuth(`${API_ENDPOINTS.CAPTAINS}?page=1&per_page=1000`)
      if (captainsRes.ok) {
        const captainsData = await captainsRes.json()
        setCaptains(captainsData.captains || [])
      }

      // Fetch recent activities
      const activitiesRes = await fetchWithAuth(
        `${API_ENDPOINTS.DASHBOARD_RECENT_ACTIVITY}?limit=5`
      )
      if (activitiesRes.ok) {
        const data = await activitiesRes.json()
        setActivities(data.activities || [])
      }

      // Fetch bookings trend
      const bookingsTrendRes = await fetchWithAuth(
        `${API_ENDPOINTS.DASHBOARD_BOOKINGS_TREND}?days=7`
      )
      if (bookingsTrendRes.ok) {
        const data = await bookingsTrendRes.json()
        setBookingsTrend(data.trend_data || [])
      }

      // Fetch revenue trend
      const revenueTrendRes = await fetchWithAuth(`${API_ENDPOINTS.DASHBOARD_REVENUE_TREND}?days=7`)
      if (revenueTrendRes.ok) {
        const data = await revenueTrendRes.json()
        setRevenueTrend(data.trend_data || [])
      }

      const operationsRes = await fetchWithAuth(API_ENDPOINTS.DASHBOARD_OPERATIONS_SUMMARY)
      if (operationsRes.ok) setDailyReport(await operationsRes.json())

      setLoading(false)
    } catch (err) {
      setError(translateApiMessage(err.message))
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    )
  }

  const translateActivity = (value) => {
    if (language !== "ar") return value
    return String(value || "")
      .replace(/new user registered/gi, "تم تسجيل مستخدم جديد")
      .replace(/new captain registered/gi, "تم تسجيل كابتن جديد")
      .replace(/booking created/gi, "تم إنشاء حجز")
      .replace(/booking completed/gi, "تم إكمال حجز")
      .replace(/captain approved/gi, "تمت الموافقة على الكابتن")
      .replace(/captain rejected/gi, "تم رفض الكابتن")
  }

  const regionalSummary = captains.reduce((summary, captain) => {
    const key = String(captain.governorate || "unassigned").toLowerCase()
    if (!summary[key]) summary[key] = { total: 0, online: 0 }
    summary[key].total += 1
    if (captain.is_online === true) summary[key].online += 1
    return summary
  }, {})

  const regionalRows = Object.entries(regionalSummary)
    .sort(([, left], [, right]) => right.online - left.online || right.total - left.total)

  return (
    <div className="space-y-8">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <section className="system-health-panel" aria-label={t("dashboard.systemHealthTitle")}>
        <div className="system-health-heading">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">{t("dashboard.systemHealthTitle")}</h2>
            <p className="text-sm text-gray-500 mt-1">{t("dashboard.systemHealthHint")}</p>
          </div>
          <Activity className="w-7 h-7 text-amber-600 opacity-70" />
        </div>
        <div className="system-health-grid">
          {[
            ["api", "systemHealthApi", Wifi],
            ["operations", "systemHealthOperations", Activity],
            ["liveMap", "systemHealthLiveMap", MapPinned],
          ].map(([key, labelKey, Icon]) => (
            <div key={key} className="system-health-item">
              <Icon className="w-5 h-5 text-gray-500" />
              <span className="flex-1 text-sm text-gray-700">{t(`dashboard.${labelKey}`)}</span>
              <span className={`system-health-status system-health-${health[key]}`}>
                {t(`dashboard.systemHealth${health[key] === "checking" ? "Checking" : health[key] === "healthy" ? "Healthy" : "Down"}`)}
              </span>
            </div>
          ))}
        </div>
        {healthUpdatedAt && <p className="text-xs text-gray-400 mt-3">{t("dashboard.systemHealthUpdated", { time: healthUpdatedAt.toLocaleTimeString() })}</p>}
      </section>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Users Card */}
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">{t("dashboard.totalUsers")}</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">{formatNumber(overview?.users?.total || 0)}</p>
              <p className="text-xs text-green-600 mt-2">
                {t("dashboard.newToday", { count: formatNumber(overview?.users?.new_today || 0) })}
              </p>
            </div>
            <Users className="w-12 h-12 text-blue-500 opacity-20" />
          </div>
        </div>

        {/* Captains Card */}
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-amber-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">{t("dashboard.totalCaptains")}</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">
                {formatNumber(overview?.captains?.total || 0)}
              </p>
              <p className="text-xs text-orange-600 mt-2">
                {t("dashboard.pendingCount", { count: formatNumber(overview?.captains?.pending || 0) })}
              </p>
            </div>
            <Truck className="w-12 h-12 text-amber-500 opacity-20" />
          </div>
        </div>

        {/* Active Bookings Card */}
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">{t("dashboard.openedBookings")}</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">
                {formatNumber(overview?.bookings?.active || 0)}
              </p>
              <p className="text-xs text-green-600 mt-2">{t("dashboard.todayCount", { count: formatNumber(overview?.bookings?.today || 0) })}</p>
            </div>
            <ShoppingCart className="w-12 h-12 text-green-500 opacity-20" />
          </div>
        </div>

        {/* Revenue Card */}
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">{t("dashboard.todayRevenue")}</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">
                {formatCurrency(overview?.revenue?.today || 0)}
              </p>
              <p className="text-xs text-purple-600 mt-2">
                {t("dashboard.commissionValue", { value: formatCurrency(overview?.revenue?.commission_today || 0) })}
              </p>
            </div>
            <TrendingUp className="w-12 h-12 text-purple-500 opacity-20" />
          </div>
        </div>
      </div>

      <section className="daily-report-panel">
        <div className="daily-report-heading">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">{t("dashboard.dailyReportTitle")}</h2>
            <p className="text-sm text-gray-500 mt-1">{t("dashboard.dailyReportHint")}</p>
          </div>
          {dailyReport.generated_at && <span className="text-xs text-gray-400">{formatDateTime(dailyReport.generated_at)}</span>}
        </div>
        <div className="daily-report-grid">
          {[
            ["active_trips", "dailyActiveTrips"], ["unassigned_bookings", "dailyUnassigned"],
            ["online_captains", "dailyOnlineCaptains"], ["completed_today", "dailyCompleted"],
            ["cancelled_today", "dailyCancelled"], ["pending_captains", "dailyPendingCaptains"],
          ].map(([key, label]) => (
            <div className="daily-report-metric" key={key}>
              <span>{t(`dashboard.${label}`)}</span>
              <strong>{formatNumber(dailyReport.counters?.[key] || 0)}</strong>
            </div>
          ))}
        </div>
        <div className="daily-report-alerts">
          <h3 className="text-sm font-semibold text-gray-800">{t("dashboard.dailyExceptions")}</h3>
          {dailyReport.alerts?.length ? dailyReport.alerts.slice(0, 5).map((alert, index) => (
            <div className="daily-report-alert" key={`${alert.type}-${alert.booking_id || alert.captain_id || index}`}>
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <span>{alert.type === "stale_captain" ? t("dashboard.exceptionStaleCaptain") : alert.type === "unassigned" ? t("dashboard.exceptionUnassigned") : t("dashboard.exceptionStuck")}</span>
              <strong>{alert.captain_name || alert.booking_id || ""}</strong>
            </div>
          )) : <div className="daily-report-clear"><CheckCircle2 className="w-4 h-4" />{t("dashboard.noExceptions")}</div>}
        </div>
      </section>

      {/* Declared service-area availability */}
      <section className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">{t("dashboard.serviceAreaAvailability")}</h2>
            <p className="text-sm text-gray-500 mt-1">{t("dashboard.serviceAreaAvailabilityHint")}</p>
          </div>
          <MapPinned className="w-8 h-8 text-amber-600 opacity-70" />
        </div>
        {regionalRows.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {regionalRows.map(([governorate, counts]) => (
              <div key={governorate} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <p className="font-semibold text-gray-800">
                  {governorate === "unassigned" ? t("dashboard.unassignedArea") : translateGovernorate(governorate)}
                </p>
                <p className="text-2xl font-bold text-green-700 mt-2">{formatNumber(counts.online)}</p>
                <p className="text-xs text-gray-600">{t("dashboard.onlineOfTotal", { total: formatNumber(counts.total) })}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-6">{t("dashboard.noAreaData")}</p>
        )}
      </section>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bookings Trend */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">{t("dashboard.bookingsTrend")}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={bookingsTrend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="bookings" stroke="#B45309" strokeWidth={2} name={t("dashboard.bookings")} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue Trend */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">{t("dashboard.revenueTrend")}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={revenueTrend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="revenue" fill="#F59E0B" name={t("dashboard.revenue")} />
              <Bar dataKey="commission" fill="#B45309" name={t("dashboard.commission")} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{t("dashboard.recentActivity")}</h3>
        <div className="space-y-4">
          {activities.length > 0 ? (
            activities.map((activity, index) => (
              <div
                key={index}
                className="flex items-start gap-4 pb-4 border-b border-gray-200 last:border-b-0">
                <div className="w-2 h-2 rounded-full bg-amber-600 mt-2 flex-shrink-0"></div>
                <div className="flex-1">
                  <p className="font-medium text-gray-800">{translateActivity(activity.title)}</p>
                  <p className="text-sm text-gray-600">{translateActivity(activity.description)}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatDateTime(activity.timestamp)}
                  </p>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    activity.status === "Active" || activity.status === "Completed"
                      ? "bg-green-100 text-green-800"
                      : activity.status === "Pending"
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-gray-100 text-gray-800"
                  }`}>
                  {translateStatus(activity.status)}
                </span>
              </div>
            ))
          ) : (
            <p className="text-gray-500 text-center py-8">{t("dashboard.noRecentActivity")}</p>
          )}
        </div>
      </div>
    </div>
  )
}
